/* ==========================================================================
   FIGS account — Log In, Sign Up, Forgot Password, Complete Profile and the
   signed-in My Account drawer, on the nav's right-side drawer shell.

   Source: Figma Account section 1838:21977 — Log In 1838:21987 (Error
   1915:6450), Password Reset Request 1915:6694, Reset Success ("Link sent")
   1932:196799, Sign Up 1838:22070 (Error 1932:194464), Complete Profile
   1932:194220 (Error 1932:194795), each with its 390 frame beside it — and
   the signed-in nav, Standard User 1174:108730 (My Account 1170:108275).
   The Password Reset page (1932:195654 / 1932:195041), where the recovery
   email lands, is experiments/09-navigation/reset-password.html, built by
   mountFigsResetPassword() below.

   mountFigsNav() mounts the drawer, so every prototype with the nav has it.
   The three Account icons and the menus' My Account links open it; a page
   can open it at a step, and so can a review link:

     import { openFigsAccount } from '/src/figs-nav/figs-nav.js';
     openFigsAccount('signup');
     …/experiments/09-navigation/?account=login|signup|forgot|sent|profile|account

   Nothing is sent anywhere. Any well-formed email and password signs in.
   Logging in, or creating an account, puts the nav in its signed-in state:
   the Account icons open My Account from then on. That state is one entry
   in localStorage (figs-lab:account), so it follows a reviewer between
   prototypes and tabs, like the bag. The My Account links open the Account
   prototype (experiments/17-account/); Preferences links to its page.
   Signed in, the shopper's saved preferences follow them too: their sizes
   are preselected on every PDP and Quick Buy (figsSavedSize(), below) and
   their embroidery is offered where ADD EMBROIDERY stands (figs-embroidery).
   A guest never sees either.
   Log Out clears the state and every form and returns to Log In.
   Every password field has Figma's Show / Hide; passwords re-mask whenever
   the drawer closes.

   The Error frames in Figma are flagged (red frame stroke) but drawn like
   the defaults, so errors use Orchard's Input Form · Empty Error: the box
   takes System/Error (#930000) and, 8 under it, the 16px Warning and
   SemiBold 11/17 copy tracked .55 — the PDP waitlist's treatment.

   What's being decided: how one drawer carries a five-step flow. Each step
   swaps in place — the current one lifts away, the title crossfades, the
   next step's rows rise in on the tight stagger — so the shopper never
   leaves the panel they're in. A bad submit marks every field at once and
   shakes the first; a good one holds for a beat of "working" dots, then
   moves on. Log Out answers in place ("Logging out"), then My Account folds
   away row by row from the bottom and Log In rises in under its title.
   Reduced motion: steps crossfade, nothing travels or shakes.
   ========================================================================== */

import { animate, stagger } from 'https://cdn.jsdelivr.net/npm/motion@12.23.12/+esm';
import './figs-account.css';

const STORE = 'figs-lab:account';
/* the nav's own drawers open and close on these (figs-nav.js, figs-cart.js) */
const DRAWER_EASE = [0.4, 0, 0.2, 1];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MIN_PASSWORD = 8;
const SHOP_PAGE = '/experiments/08-d-homepage/'; // "return to shopping": the Homepage prototype
/* the Account prototype (experiments/17-account/): My Account's links land
   on its screens; Preferences links to the saved-sizes page. */
const ACCOUNT_PAGE = '/experiments/17-account/';

const icon = (name) => new URL(`./assets/account/${name}`, import.meta.url).href;
const ICON = {
  close: icon('icon-close.svg'),
  parcel: icon('icon-parcel.svg'),
  colors: icon('icon-color-wheel.svg'),
  heart: icon('icon-heart.svg'),
  radioOff24: icon('radio-inactive-24.svg'),
  radioOn24: icon('radio-active-24.svg'),
  radioOff16: icon('radio-inactive-16.svg'),
  check16: icon('circle-check-16.svg'),
  chevron: icon('icon-chevron-down-16.svg'),
  warning: icon('icon-warning-error.svg'),
};

/* The drawer's steps and their titles (uppercased by CSS, like Figma's UPPER) */
const STEPS = {
  login: 'Log in',
  signup: 'Sign up',
  forgot: 'Forgot your password?',
  sent: 'Link sent',
  profile: 'Complete your profile',
  account: 'My account',
};

const OCCUPATIONS = [
  'Registered Nurse (RN)', 'Licensed Practical Nurse (LPN)', 'Nurse Practitioner (NP)',
  'Certified Nursing Assistant (CNA)', 'Physician (MD / DO)', 'Physician Assistant (PA)',
  'Resident / Fellow', 'Medical Student', 'Nursing Student', 'Medical Assistant',
  'Surgical Technologist', 'EMT / Paramedic', 'Pharmacist', 'Physical / Occupational Therapist',
  'Dentist', 'Dental Hygienist / Assistant', 'Veterinarian', 'Veterinary Technician', 'Other',
];

const ERR = {
  email: 'Please enter a valid email address.',
  password: 'Please enter your password.',
  firstName: 'Please enter your first name.',
  lastName: 'Please enter your last name.',
  newPassword: `Your password must be at least ${MIN_PASSWORD} characters.`,
  confirm: 'Your passwords don’t match.',
  confirmEmpty: 'Please confirm your new password.',
  shopping: 'Please choose who you’re shopping for.',
  occupation: 'Please select an occupation.',
  birthday: 'Please enter your birthday as MM / DD.',
};

/* motion.css tokens where the page loads them (PDP, Uniform Builder), the
   file's own values where it doesn't (PLPs, Homepage, Navigation). Read per
   use, so a timing tuned in the Vocabulary playground applies here too. */
function mo() {
  const cs = getComputedStyle(document.documentElement);
  const sec = (name, fallback) => {
    const v = cs.getPropertyValue(name).trim();
    if (!v) return fallback;
    return v.endsWith('ms') ? parseFloat(v) / 1000 : parseFloat(v);
  };
  const px = (name, fallback) => {
    const v = parseFloat(cs.getPropertyValue(name));
    return Number.isFinite(v) ? v : fallback;
  };
  const ease = (name, fallback) => {
    const m = cs.getPropertyValue(name).match(/-?[\d.]+/g);
    return m && m.length === 4 ? m.map(Number) : fallback;
  };
  return {
    quick: sec('--mo-quick', 0.2),
    base: sec('--mo-base', 0.32),
    slow: sec('--mo-slow', 0.56),
    stagTight: sec('--mo-stagger-tight', 0.04),
    riseXs: px('--mo-rise-xs', 8),
    riseSm: px('--mo-rise-sm', 16),
    eStandard: ease('--mo-ease-standard', [0.4, 0, 0.2, 1]),
    eEntrance: ease('--mo-ease-entrance', [0.16, 1, 0.3, 1]),
    eExit: ease('--mo-ease-exit', [0.7, 0, 0.84, 0]),
  };
}

/* the OS setting, or the lab bar's Reduced toggle (mirrored onto <html data-reduced>) */
const isReduced = () =>
  matchMedia('(prefers-reduced-motion: reduce)').matches ||
  document.documentElement.dataset.reduced === 'true';

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
/* an animation's end, or its expected end — a hidden tab runs no frames, and
   a step change must never wait on one */
const settled = (anim, ms) => Promise.race([anim?.finished?.catch(() => {}) ?? Promise.resolve(), wait(ms)]);

/* ---------------------------------------------------------------------------
   Markup
   ------------------------------------------------------------------------ */
const warning = `<img src="${ICON.warning}" alt="" width="17" height="16">`;
const errorRow = (id, key) =>
  `<p class="fnav-account-error" id="${id}-err" data-error-for="${key}" hidden>${warning}<span></span></p>`;

/* Underlined Input Form (Default): one ruled field and its error row */
function field(form, { name, type = 'text', placeholder, label, autocomplete = 'off', inputmode = '', maxlength = 0, show = false }) {
  const id = `fnav-account-${form}-${name}`;
  return `<div class="fnav-account-field">
      <label class="fnav-account-input" for="${id}">
        <span class="fnav-account-sr">${label}</span>
        <input id="${id}" name="${name}" type="${type}" placeholder="${placeholder}" autocomplete="${autocomplete}"${inputmode ? ` inputmode="${inputmode}"` : ''}${maxlength ? ` maxlength="${maxlength}"` : ''} spellcheck="false" aria-describedby="${id}-err">
        ${show ? `<button class="fnav-account-show" type="button" data-show="${id}" aria-controls="${id}" aria-pressed="false" aria-label="Show password">Show</button>` : ''}
      </label>
      ${errorRow(id, name)}
    </div>`;
}

const PROPS = `<ul class="fnav-account-props">
      <li><img src="${ICON.parcel}" alt="" width="24" height="24"><span>Quick Reorders</span></li>
      <li><img src="${ICON.colors}" alt="" width="24" height="24"><span>Saved Preferences</span></li>
      <li><img src="${ICON.heart}" alt="" width="24" height="24"><span>Save Your Favorites</span></li>
    </ul>`;

const submitBtn = (label) =>
  `<button class="fnav-account-submit" type="submit"><span class="fnav-account-submit-label" data-label="${label}">${label}</span></button>`;

const choice = (name, value, label) => `<label class="fnav-account-choice">
          <input class="fnav-account-toggle" type="radio" name="${name}" value="${value}">
          <span class="fnav-account-toggle-icon" aria-hidden="true"><img class="is-off" src="${ICON.radioOff16}" alt="" width="16" height="16"><img class="is-on" src="${ICON.check16}" alt="" width="16" height="16"></span>
          <span>${label}</span>
        </label>`;

const DRAWER_HTML = `
<div class="fnav-account-drawer-scrim" id="fnav-account-drawer-scrim"></div>
<div class="fnav-account-drawer" id="fnav-account-drawer" role="dialog" aria-modal="true" aria-labelledby="fnav-account-drawer-title" aria-hidden="true" inert>
  <div class="fnav-account-head">
    <h2 class="fnav-account-title" id="fnav-account-drawer-title" tabindex="-1">Log in</h2>
    <button class="fnav-account-close" id="fnav-account-drawer-close" type="button" aria-label="Close"><img src="${ICON.close}" alt="" width="24" height="24"></button>
  </div>

  <form class="fnav-account-step" data-step="login" novalidate hidden>
    <p class="fnav-account-copy">Don’t have an account? <a href="#" data-go="signup">Sign up</a></p>
    ${PROPS}
    <div class="fnav-account-fields">
      ${field('login', { name: 'email', type: 'email', placeholder: 'Enter Email Address', label: 'Email address', autocomplete: 'username', inputmode: 'email' })}
      ${field('login', { name: 'password', type: 'password', placeholder: 'Enter Password', label: 'Password', autocomplete: 'current-password', show: true })}
    </div>
    ${submitBtn('Log in')}
    <p class="fnav-account-copy">Forgot your password? <a href="#" data-go="forgot">Click here</a></p>
  </form>

  <form class="fnav-account-step" data-step="signup" novalidate hidden>
    <p class="fnav-account-copy">Already have an account? <a href="#" data-go="login">Log In</a></p>
    ${PROPS}
    <div class="fnav-account-fields">
      ${field('signup', { name: 'firstName', placeholder: 'First Name', label: 'First name', autocomplete: 'given-name' })}
      ${field('signup', { name: 'lastName', placeholder: 'Last Name', label: 'Last name', autocomplete: 'family-name' })}
      ${field('signup', { name: 'email', type: 'email', placeholder: 'Enter Email Address', label: 'Email address', autocomplete: 'email', inputmode: 'email' })}
      ${field('signup', { name: 'password', type: 'password', placeholder: 'Password', label: 'Password', autocomplete: 'new-password', show: true })}
    </div>
    ${submitBtn('Sign up')}
    <label class="fnav-account-optin">
      <input class="fnav-account-toggle" type="checkbox" name="optIn">
      <span class="fnav-account-toggle-icon" aria-hidden="true"><img class="is-off" src="${ICON.radioOff24}" alt="" width="24" height="24"><img class="is-on" src="${ICON.radioOn24}" alt="" width="24" height="24"></span>
      <span class="fnav-account-optin-copy">Yes, I would like to receive emails from FIGS about products, updates, and exclusive offers and promotions. You can unsubscribe at any time. See our <a href="#">Privacy Policy</a>.</span>
    </label>
  </form>

  <form class="fnav-account-step" data-step="forgot" novalidate hidden>
    <p class="fnav-account-copy">Enter and confirm a new password below to reset your password.</p>
    ${field('forgot', { name: 'email', type: 'email', placeholder: 'Enter Email Address', label: 'Email address', autocomplete: 'email', inputmode: 'email' })}
    ${submitBtn('Reset password')}
    <p class="fnav-account-copy"><a href="#" data-go="login">Nevermind</a></p>
  </form>

  <div class="fnav-account-step" data-step="sent" hidden>
    <p class="fnav-account-copy">If there’s an account associated to this email you will receive a recovery link. Please allow a few minutes while the email is in transit.</p>
    <p class="fnav-account-copy"><a href="#" data-close>Return to shopping</a></p>
    <p class="fnav-account-copy">Didn’t receive a link?</p>
    <p class="fnav-account-copy"><a href="#" data-resend>Send again</a></p>
    <p class="fnav-account-copy"><a href="#">Get help</a></p>
  </div>

  <form class="fnav-account-step" data-step="profile" novalidate hidden>
    <p class="fnav-account-copy" id="fnav-account-shopping-label">I’m shopping for:</p>
    <div class="fnav-account-field">
      <div class="fnav-account-choices" role="radiogroup" aria-labelledby="fnav-account-shopping-label" aria-describedby="fnav-account-profile-shopping-err">
        ${choice('shopping', 'women', 'Women')}
        ${choice('shopping', 'men', 'Men')}
      </div>
      ${errorRow('fnav-account-profile-shopping', 'shopping')}
    </div>
    <p class="fnav-account-copy" id="fnav-account-occupation-label">My occupation is:</p>
    <div class="fnav-account-field">
      <label class="fnav-account-select">
        <select name="occupation" aria-labelledby="fnav-account-occupation-label" aria-describedby="fnav-account-profile-occupation-err">
          <option value="" selected disabled>Select an occupation</option>
          ${OCCUPATIONS.map((o) => `<option>${o}</option>`).join('')}
        </select>
        <img src="${ICON.chevron}" alt="" width="16" height="16">
      </label>
      ${errorRow('fnav-account-profile-occupation', 'occupation')}
    </div>
    <p class="fnav-account-copy" id="fnav-account-birthday-label">My birthday is:</p>
    <div class="fnav-account-field">
      <div class="fnav-account-birthday" role="group" aria-labelledby="fnav-account-birthday-label" aria-describedby="fnav-account-profile-birthday-err">
        <label class="fnav-account-input"><span class="fnav-account-sr">Month</span><input name="month" type="text" placeholder="MM" inputmode="numeric" maxlength="2" autocomplete="bday-month"></label>
        <label class="fnav-account-input"><span class="fnav-account-sr">Day</span><input name="day" type="text" placeholder="DD" inputmode="numeric" maxlength="2" autocomplete="bday-day"></label>
      </div>
      ${errorRow('fnav-account-profile-birthday', 'birthday')}
    </div>
    ${submitBtn('Save &amp; continue')}
    <p class="fnav-account-copy"><a href="#" data-skip>Skip for now</a></p>
  </form>

  <div class="fnav-account-step" data-step="account" hidden>
    <nav class="fnav-account-links" aria-label="My account" data-fold-rows>
      <a href="${ACCOUNT_PAGE}">Profile</a>
      <a href="${ACCOUNT_PAGE}orders.html">Orders</a>
      <a href="${ACCOUNT_PAGE}preferences.html">Preferences</a>
      <a href="${ACCOUNT_PAGE}addresses.html">Addresses</a>
      <a href="${ACCOUNT_PAGE}favorites.html">Favorites</a>
    </nav>
    <hr class="fnav-account-divider">
    <button class="fnav-account-logout" type="button" data-logout><span class="fnav-account-logout-label" data-label="Log Out">Log Out</span></button>
  </div>

  <p class="fnav-account-sr" aria-live="polite" data-live></p>
</div>`;

/* ---------------------------------------------------------------------------
   Session — signed in or not, and the sizes the shopper saved in My Account
   → Preferences (only once they've changed them; until then the Figma
   frame's own). Nothing else is kept.
   ------------------------------------------------------------------------ */
let session = load();

function load() {
  try {
    const v = JSON.parse(localStorage.getItem(STORE) || 'null');
    return v && v.signedIn ? v : null;
  } catch {
    return null;
  }
}
function save() {
  try {
    if (session) localStorage.setItem(STORE, JSON.stringify(session));
    else localStorage.removeItem(STORE);
  } catch { /* private mode: the session lasts the page */ }
}

export const isFigsSignedIn = () => Boolean(session);

/* Saved sizes, as My Account → Preferences shows them (Figma 1980:169933):
   women's tops Small Regular, pants Small Petite, outerwear Small, shoes 9;
   no underscrubs, nothing for men. The Account prototype's Size Preferences
   drawer reads and saves them here. */
const SIZE_PREFS = {
  gender: 'women',
  autofilter: false,
  women: { tops: { size: 'S', variant: 'Regular' }, pants: { size: 'S', variant: 'Petite' }, outerwear: { size: 'S' }, underscrubs: {}, shoes: { size: '9' } },
  men: { tops: {}, pants: {}, outerwear: {}, underscrubs: {}, shoes: {} },
};
export const getFigsSizePrefs = () => structuredClone(session?.sizes || SIZE_PREFS);
export function setFigsSizePrefs(prefs) {
  if (!session) return;
  session = { ...session, sizes: structuredClone(prefs) };
  save();
  document.dispatchEvent(new CustomEvent('figs-account:change', { detail: { signedIn: true, sizes: true } }));
}

/* Which saved size a product takes: its category from what the page knows
   (a size chart or the Uniform Builder's layer, a set's role, else the
   name, else its URL), women's unless the name or URL says men's, and only
   a size / type the product actually comes in. Null for a guest, or when
   nothing saved fits. */
const CATEGORY_BY = {
  chart: { tops: 'tops', pants: 'pants', 'mens-tops': 'tops', 'mens-pants': 'pants', outerwear: 'outerwear', underscrubs: 'underscrubs', shoes: 'shoes', footwear: 'shoes' },
  role: { top: 'tops', pant: 'pants', bottom: 'pants', layer: 'outerwear', jacket: 'outerwear', vest: 'outerwear' },
};
const CATEGORY_NAMES = [
  ['shoes', /\b(shoes?|sneakers?|clogs?|new balance|nb \d+)\b/i],
  ['underscrubs', /underscrub|base ?layer|\btee\b/i],
  ['outerwear', /jacket|vest|bomber|puffer|fleece|coat|anorak/i],
  ['pants', /\b(pants?|joggers?|leggings?|shorts|scrub bottoms?|jumpsuit)\b/i],
  ['tops', /\b(tops?|shirts?)\b/i],
];
export function figsSavedSize({ name = '', url = '', chart, layer, role, gender, sizes = [], types = [] } = {}) {
  if (!session) return null;
  const handle = String(url).split('/').pop().split('?')[0].replace(/-/g, ' ');
  const category = CATEGORY_BY.chart[chart] || CATEGORY_BY.chart[layer] || CATEGORY_BY.role[String(role || '').toLowerCase()]
    || CATEGORY_NAMES.find(([, re]) => re.test(name))?.[0] || CATEGORY_NAMES.find(([, re]) => re.test(handle))?.[0];
  if (!category) return null;
  const who = gender || (/\bmen'?s\b|\/mens-/i.test(`${name} ${url}`) ? 'men' : 'women');
  const pick = getFigsSizePrefs()[who]?.[category] || {};
  // unisex shoe sizes read "M7.5/W9": a women's 9 is the W half, a men's the M
  const half = (s) => s.split('/').some((part) => part === `${who === 'men' ? 'M' : 'W'}${pick.size}`);
  const size = !pick.size ? null
    : sizes.includes(pick.size) ? pick.size
      : category === 'shoes' ? sizes.find(half) || null : null;
  if (!size) return null;
  return { size, type: pick.variant && types.includes(pick.variant) ? pick.variant : null };
}

function signIn(details) {
  session = { signedIn: true, ...details };
  save();
  syncIcons();
  document.dispatchEvent(new CustomEvent('figs-account:change', { detail: { signedIn: true } }));
}
function signOut() {
  session = null;
  save();
  syncIcons();
  document.dispatchEvent(new CustomEvent('figs-account:change', { detail: { signedIn: false } }));
}

/* ---------------------------------------------------------------------------
   Drawer
   ------------------------------------------------------------------------ */
let ui = null;
let hooks = { lock() {}, unlock() {}, closeOthers() {} };
let open = false;
let step = null;
let lastFocus = null;
let busy = false;
let turn = 0; // bumps on every step change, so a late animation can't undo a newer one
let drawerAnim = null;
let scrimAnim = null;

const ACCOUNT_ICONS = '.fnav-icon-account';

/* The Account icons are bare SVGs in the nav's markup; each is wrapped in a
   button, like the Bag icons, so it can be reached and announced. */
function wrapAccountIcons() {
  document.querySelectorAll(ACCOUNT_ICONS).forEach((svg) => {
    if (svg.parentElement.classList.contains('fnav-account-btn')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fnav-account-btn';
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.setAttribute('aria-controls', 'fnav-account-drawer');
    svg.replaceWith(btn);
    svg.removeAttribute('aria-label');
    svg.setAttribute('aria-hidden', 'true');
    btn.append(svg);
  });
  return [...document.querySelectorAll('.fnav-account-btn')];
}
function syncIcons() {
  wrapAccountIcons().forEach((btn) => {
    btn.setAttribute('aria-label', session ? 'My account' : 'Account: log in or sign up');
    btn.dataset.signedIn = String(Boolean(session));
  });
}

const stepEl = (name) => ui.drawer.querySelector(`[data-step="${name}"]`);
const visibleRows = (el) => [...el.children].filter((c) => !c.hidden);

function announce(msg) {
  ui.live.textContent = '';
  requestAnimationFrame(() => { ui.live.textContent = msg; });
  setTimeout(() => { if (!ui.live.textContent) ui.live.textContent = msg; }, 60);
}

/* The rows of a step rise in on the tight stagger, as the drawer's content
   does when it opens. Inline styles are cleared at the expected end, so the
   rows show even if no frame ever ran. */
function riseIn(el, { delay = 0 } = {}) {
  const rows = visibleRows(el);
  if (!rows.length) return;
  const m = mo();
  const reduced = isReduced();
  const anim = animate(
    rows,
    reduced ? { opacity: [0, 1] } : { opacity: [0, 1], y: [m.riseSm, 0] },
    { duration: reduced ? m.quick : m.base, ease: m.eEntrance, delay: stagger(m.stagTight, { startDelay: delay }) }
  );
  const end = (delay + m.stagTight * rows.length + m.base) * 1000 + 120;
  const mine = turn;
  settled(anim, end).then(() => {
    if (mine !== turn) return;
    rows.forEach((r) => { r.style.opacity = ''; r.style.transform = ''; });
  });
}

function show(name) {
  Object.keys(STEPS).forEach((k) => { stepEl(k).hidden = k !== name; });
  step = name;
  ui.title.textContent = STEPS[name];
}

/* A step's rows, one level down where a group asks for it (My Account's
   links fold one by one, not as a block) */
const foldRows = (el) => visibleRows(el).flatMap((r) => ('foldRows' in r.dataset ? [...r.children] : [r]));

/* Swap the drawer to another step. The current step leaves — as one block,
   or with `fold` row by row from the bottom up — and the title fades out on
   the same beat, so the header never shows the old name over the new rows.
   Then the new title and the next step's rows come in together. */
async function go(name, { focus = true, announceIt = '', fold = false } = {}) {
  if (!ui || !STEPS[name]) return;
  if (name === step) return;
  const mine = ++turn;
  const from = step && stepEl(step);
  const next = stepEl(name);
  const m = mo();
  const reduced = isReduced();
  const retitle = ui.title.textContent !== STEPS[name];
  step = name;
  if (from && !from.hidden) {
    const rows = fold ? foldRows(from).reverse() : [from];
    const lag = fold && !reduced ? m.stagTight * 1.5 : 0; // a touch slower than the rise, so the fold reads
    const out = reduced ? { opacity: [1, 0] } : { opacity: [1, 0], y: [0, -m.riseXs] };
    const exit = animate(rows, out, { duration: m.quick, ease: m.eExit, delay: lag ? stagger(lag) : 0 });
    const span = m.quick + lag * (rows.length - 1);
    if (retitle) animate(ui.title, { opacity: [1, 0] }, { duration: m.quick, ease: m.eExit, delay: span - m.quick });
    await settled(exit, span * 1000 + 60);
    if (mine !== turn) return;
    from.hidden = true;
    [from, ...rows].forEach((r) => { r.style.opacity = ''; r.style.transform = ''; });
  }
  Object.keys(STEPS).forEach((k) => { if (k !== name) stepEl(k).hidden = true; });
  ui.title.textContent = STEPS[name];
  if (retitle && from) {
    settled(animate(ui.title, { opacity: [0, 1] }, { duration: m.base, ease: m.eEntrance }), m.base * 1000 + 60)
      .then(() => { if (mine === turn) ui.title.style.opacity = ''; });
  }
  next.hidden = false;
  ui.drawer.scrollTop = 0;
  riseIn(next);
  if (announceIt) announce(announceIt);
  if (focus) focusStep(next);
}

/* Focus follows the step: its first field, or the title when it has none */
function focusStep(el) {
  const first = el.querySelector('input:not([type="hidden"]):not(.fnav-account-toggle), select');
  const coarse = matchMedia('(pointer: coarse)').matches; // no keyboard springing up on phones
  if (first && !coarse) first.focus({ preventScroll: true });
  else ui.title.focus({ preventScroll: true });
}

export function openFigsAccount(to, { fromLink = false } = {}) {
  if (!ui) return;
  const target = STEPS[to] ? to : (session ? 'account' : 'login');
  if (open) { go(target); return; }
  hooks.closeOthers('account');
  open = true;
  turn++;
  hooks.lock();
  lastFocus = document.activeElement;

  // under the promo bar while it shows, like the bag and the country drawer
  const promoBar = document.getElementById('fnav-promo-bar');
  const top = promoBar ? Math.max(0, promoBar.getBoundingClientRect().bottom) : 0;
  ui.drawer.style.top = `${top}px`;
  ui.scrim.style.top = `${top}px`;

  show(target);
  ui.drawer.scrollTop = 0;
  ui.drawer.inert = false;
  ui.drawer.setAttribute('aria-hidden', 'false');
  ui.scrim.style.pointerEvents = 'auto';
  ui.drawer.style.pointerEvents = 'auto';
  drawerAnim?.cancel?.();
  scrimAnim?.cancel?.();
  ui.drawer.style.opacity = '1';
  ui.drawer.style.transform = 'translateX(0%)';
  ui.scrim.style.opacity = '1';

  if (isReduced()) {
    drawerAnim = animate(ui.drawer, { opacity: [0, 1] }, { duration: 0.2, ease: DRAWER_EASE });
  } else {
    drawerAnim = animate(ui.drawer, { opacity: [0, 1], transform: ['translateX(100%)', 'translateX(0%)'] }, { duration: 0.3, ease: DRAWER_EASE });
  }
  scrimAnim = animate(ui.scrim, { opacity: [0, 1] }, { duration: 0.3, ease: DRAWER_EASE });
  riseIn(stepEl(target), { delay: 0.12 }); // the panel arrives first, then its rows
  // a review link opens it on load, before anyone has used the page: no focus ring
  if (!fromLink) setTimeout(() => { if (open) ui.close.focus({ preventScroll: true }); }, 60);
}

export function closeFigsAccount() {
  if (!ui || !open) return;
  open = false;
  turn++;
  hooks.unlock();
  ui.drawer.setAttribute('aria-hidden', 'true');
  ui.drawer.inert = true;
  ui.scrim.style.pointerEvents = 'none';
  ui.drawer.style.pointerEvents = 'none';
  drawerAnim?.cancel?.();
  scrimAnim?.cancel?.();
  ui.drawer.style.opacity = '0';
  ui.drawer.style.transform = 'translateX(100%)';
  ui.scrim.style.opacity = '0';
  if (isReduced()) {
    drawerAnim = animate(ui.drawer, { opacity: [1, 0] }, { duration: 0.2, ease: DRAWER_EASE });
  } else {
    drawerAnim = animate(ui.drawer, { opacity: [1, 0], transform: ['translateX(0%)', 'translateX(100%)'] }, { duration: 0.25, ease: DRAWER_EASE });
  }
  scrimAnim = animate(ui.scrim, { opacity: [1, 0] }, { duration: 0.25, ease: DRAWER_EASE });
  // the next open starts clean: no error from this visit waiting in a form
  setTimeout(() => {
    if (open) return;
    ui.drawer.querySelectorAll('form').forEach((f) => clearErrors(f));
    maskAll(ui.drawer);
  }, 300);
  if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  lastFocus = null;
}

/* ---------------------------------------------------------------------------
   Forms — validated on submit, not while typing. A field that's been marked
   clears as soon as it's fixed.
   ------------------------------------------------------------------------ */
const val = (form, name) => (form.elements[name]?.value || '').trim();

function birthdayOk(form) {
  const mm = val(form, 'month');
  const dd = val(form, 'day');
  if (!/^\d{1,2}$/.test(mm) || !/^\d{1,2}$/.test(dd)) return false;
  const month = Number(mm);
  const day = Number(dd);
  const days = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1];
}

/* key → [check(form) → true | message] per form */
const RULES = {
  login: {
    email: (f) => EMAIL.test(val(f, 'email')) || ERR.email,
    password: (f) => f.elements.password.value.length > 0 || ERR.password,
  },
  signup: {
    firstName: (f) => Boolean(val(f, 'firstName')) || ERR.firstName,
    lastName: (f) => Boolean(val(f, 'lastName')) || ERR.lastName,
    email: (f) => EMAIL.test(val(f, 'email')) || ERR.email,
    password: (f) => f.elements.password.value.length >= MIN_PASSWORD || ERR.newPassword,
  },
  forgot: {
    email: (f) => EMAIL.test(val(f, 'email')) || ERR.email,
  },
  profile: {
    shopping: (f) => Boolean(f.querySelector('[name="shopping"]:checked')) || ERR.shopping,
    occupation: (f) => Boolean(f.elements.occupation.value) || ERR.occupation,
    birthday: (f) => birthdayOk(f) || ERR.birthday,
  },
  reset: {
    password: (f) => f.elements.password.value.length >= MIN_PASSWORD || ERR.newPassword,
    confirm: (f) => {
      if (!f.elements.confirm.value) return ERR.confirmEmpty;
      return f.elements.confirm.value === f.elements.password.value || ERR.confirm;
    },
  },
};

/* the controls a key covers, and the box that turns red */
function controlsFor(form, key) {
  if (key === 'shopping') return [...form.querySelectorAll('[name="shopping"]')];
  if (key === 'birthday') return [form.elements.month, form.elements.day];
  return [form.elements[key]].filter(Boolean);
}
const boxOf = (control) => control.closest('.fnav-account-input, .fnav-account-select');

function setError(form, key, message) {
  const row = form.querySelector(`[data-error-for="${key}"]`);
  if (!row) return;
  const controls = controlsFor(form, key);
  const on = Boolean(message);
  controls.forEach((c) => {
    if (on) c.setAttribute('aria-invalid', 'true');
    else c.removeAttribute('aria-invalid');
    boxOf(c)?.classList.toggle('is-invalid', on);
  });
  const m = mo();
  if (on) {
    row.querySelector('span').textContent = message;
    if (row.hidden) {
      row.hidden = false;
      animate(row, isReduced() ? { opacity: [0, 1] } : { opacity: [0, 1], y: [-4, 0] }, { duration: m.base, ease: m.eEntrance });
    }
  } else if (!row.hidden) {
    settled(animate(row, { opacity: [1, 0] }, { duration: m.quick, ease: m.eExit }), m.quick * 1000 + 60).then(() => {
      if (!controls.some((c) => c.getAttribute('aria-invalid') === 'true')) {
        row.hidden = true;
        row.style.opacity = '';
        row.style.transform = '';
      }
    });
  }
}

function clearErrors(form) {
  form.querySelectorAll('[data-error-for]').forEach((row) => {
    row.hidden = true;
    row.style.opacity = '';
    row.style.transform = '';
  });
  form.querySelectorAll('[aria-invalid]').forEach((c) => c.removeAttribute('aria-invalid'));
  form.querySelectorAll('.is-invalid').forEach((b) => b.classList.remove('is-invalid'));
}

/* every rule at once, so the shopper sees all that's missing in one pass */
function validate(form, rules) {
  const failed = [];
  Object.entries(rules).forEach(([key, check]) => {
    const result = check(form);
    const message = result === true ? '' : result;
    setError(form, key, message);
    if (message) failed.push([key, message]);
  });
  return failed;
}

function shake(el) {
  if (!el || isReduced()) return;
  animate(el, { x: [0, -6, 6, -4, 4, 0] }, { duration: mo().slow * 0.75, ease: mo().eStandard });
}

/* a field marked wrong clears as soon as it's right */
function watchFixes(form, rules) {
  const recheck = (e) => {
    const key = Object.keys(rules).find((k) => controlsFor(form, k).includes(e.target));
    if (!key || !controlsFor(form, key).some((c) => c.getAttribute('aria-invalid') === 'true')) return;
    if (rules[key](form) === true) setError(form, key, '');
  };
  form.addEventListener('input', recheck);
  form.addEventListener('change', recheck);
}

const DOTS = '<span class="fnav-account-dots" aria-hidden="true"><i></i><i></i><i></i></span>';

/* A beat of "working": the button's label steps aside for three dots (or
   `busy`, e.g. "Logging out" and the dots) */
async function working(btn, busyHTML = DOTS) {
  const label = btn.querySelector('[data-label]');
  const m = mo();
  const reduced = isReduced();
  btn.setAttribute('aria-busy', 'true');
  if (!reduced) await settled(animate(label, { opacity: [1, 0], y: [0, -6] }, { duration: m.quick, ease: m.eExit }), m.quick * 1000 + 60);
  label.innerHTML = busyHTML;
  if (!reduced) animate(label, { opacity: [0, 1], y: [6, 0] }, { duration: m.base, ease: m.eEntrance });
  await wait(reduced ? 300 : 700);
  return () => {
    btn.removeAttribute('aria-busy');
    label.innerHTML = label.dataset.label;
    label.style.opacity = '';
    label.style.transform = '';
  };
}

/* Run a form: validate, and on a clean pass hold the beat, then `done` */
function handleSubmit(form, rules, done) {
  watchFixes(form, rules);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (busy) return;
    const failed = validate(form, rules);
    if (failed.length) {
      const first = controlsFor(form, failed[0][0])[0];
      shake(boxOf(first) || first.closest('.fnav-account-field'));
      if (!matchMedia('(pointer: coarse)').matches || first.type !== 'radio') first.focus({ preventScroll: false });
      announce(failed.map(([, msg]) => msg).join(' '));
      return;
    }
    busy = true;
    const restore = await working(form.querySelector('.fnav-account-submit'));
    try {
      await done(form);
    } finally {
      // the button resets once its step has left
      setTimeout(restore, 400);
      busy = false;
    }
  });
}

/* Show / Hide on a password field (Figma: "Show", Medium 11 underlined, at
   the box's right). The caret stays in the field when it's the one in use. */
function setShown(btn, shown) {
  const input = document.getElementById(btn.dataset.show);
  if (!input) return;
  input.type = shown ? 'text' : 'password';
  btn.textContent = shown ? 'Hide' : 'Show';
  btn.setAttribute('aria-pressed', String(shown));
  btn.setAttribute('aria-label', shown ? 'Hide password' : 'Show password');
}
function wireShowToggles(root) {
  root.addEventListener('pointerdown', (e) => {
    const btn = e.target.closest('[data-show]');
    if (btn && document.activeElement?.id === btn.dataset.show) e.preventDefault();
  });
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-show]');
    if (!btn) return;
    e.preventDefault();
    setShown(btn, btn.getAttribute('aria-pressed') !== 'true');
  });
}
/* every password back to dots: on close, on Log Out, after a reset */
const maskAll = (root) => root.querySelectorAll('[data-show]').forEach((btn) => setShown(btn, false));

function resetForms() {
  ui.drawer.querySelectorAll('form').forEach((f) => {
    f.reset();
    clearErrors(f);
  });
  maskAll(ui.drawer);
}

/* Log Out: the link answers first ("Logging out" and the dots), the session
   clears, then My Account folds away from the bottom up and Log In rises in
   under its title — so the change of state reads as one clear step back. */
async function logOut(btn) {
  if (busy) return;
  busy = true;
  const restore = await working(btn, `Logging out${DOTS}`);
  signOut();
  resetForms();
  await go('login', { fold: true, announceIt: 'You’ve logged out.' });
  restore();
  busy = false;
}

/* ---------------------------------------------------------------------------
   Mount
   ------------------------------------------------------------------------ */
export function mountFigsAccount({ lockBodyScroll, unlockBodyScroll, closeOtherOverlays } = {}) {
  if (ui) return;
  hooks = {
    lock: lockBodyScroll || hooks.lock,
    unlock: unlockBodyScroll || hooks.unlock,
    closeOthers: closeOtherOverlays || hooks.closeOthers,
  };
  const t = document.createElement('template');
  t.innerHTML = DRAWER_HTML.trim();
  document.body.append(t.content);
  const drawer = document.getElementById('fnav-account-drawer');
  ui = {
    drawer,
    scrim: document.getElementById('fnav-account-drawer-scrim'),
    close: document.getElementById('fnav-account-drawer-close'),
    title: document.getElementById('fnav-account-drawer-title'),
    live: drawer.querySelector('[data-live]'),
  };
  show(session ? 'account' : 'login');

  syncIcons();
  wrapAccountIcons().forEach((btn) => btn.addEventListener('click', (e) => {
    e.preventDefault();
    openFigsAccount();
  }));
  document.querySelectorAll('[data-fnav-account-open]').forEach((a) => a.addEventListener('click', (e) => {
    e.preventDefault();
    openFigsAccount();
  }));

  wireShowToggles(drawer);
  ui.close.addEventListener('click', closeFigsAccount);
  ui.scrim.addEventListener('click', closeFigsAccount);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) closeFigsAccount(); });
  document.addEventListener('overlay:open', (e) => { if (e.detail.except !== 'account') closeFigsAccount(); });

  drawer.addEventListener('click', (e) => {
    const a = e.target.closest('a, button');
    if (!a || !drawer.contains(a)) return;
    if (a.dataset.go) {
      e.preventDefault();
      // Forgot Password picks up the email already typed into Log In
      if (a.dataset.go === 'forgot') {
        const typed = val(stepEl('login'), 'email');
        const forgot = stepEl('forgot');
        if (typed && !forgot.elements.email.value) forgot.elements.email.value = typed;
      }
      go(a.dataset.go);
    } else if ('close' in a.dataset) {
      e.preventDefault();
      closeFigsAccount();
    } else if ('resend' in a.dataset) {
      e.preventDefault();
      turn++;
      riseIn(stepEl('sent'));
      announce('We’ve sent you another recovery link.');
    } else if ('skip' in a.dataset) {
      e.preventDefault();
      go('account', { announceIt: 'You’re signed in.' });
    } else if ('logout' in a.dataset) {
      logOut(a);
    } else if (a.matches('a[href="#"]')) {
      // placeholder links (My Account's, Get help, Privacy Policy) go nowhere,
      // and shouldn't scroll the page under the drawer either
      e.preventDefault();
    }
  });

  handleSubmit(stepEl('login'), RULES.login, async (f) => {
    signIn({ email: val(f, 'email') });
    await go('account', { announceIt: 'You’re signed in.' });
  });
  handleSubmit(stepEl('signup'), RULES.signup, async (f) => {
    signIn({ email: val(f, 'email'), firstName: val(f, 'firstName') });
    await go('profile', { announceIt: 'Your account is ready. Complete your profile, or skip for now.' });
  });
  handleSubmit(stepEl('forgot'), RULES.forgot, async () => {
    await go('sent', { announceIt: 'Link sent.' });
  });
  handleSubmit(stepEl('profile'), RULES.profile, async () => {
    await go('account', { announceIt: 'Profile saved.' });
  });

  /* another tab (or another prototype open beside this one) signed in or out */
  addEventListener('storage', (e) => {
    if (e.key !== STORE) return;
    session = load();
    syncIcons();
    if (!open) show(session ? 'account' : 'login');
    else if (!session && step === 'account') go('login', { focus: false });
  });

  /* review links: ?account=<step> opens the drawer there */
  const asked = new URLSearchParams(location.search).get('account');
  if (asked && STEPS[asked]) setTimeout(() => openFigsAccount(asked, { fromLink: true }), 0);
}

/* ---------------------------------------------------------------------------
   Password Reset page — the recovery email's landing page. Mounts the form
   into `el` (the page's own placeholder) under the nav. A good reset opens
   the Log In drawer on the page so the new password can be used.
   ------------------------------------------------------------------------ */
export function mountFigsResetPassword(el) {
  const page = document.createElement('main');
  page.className = 'fnav-reset';
  page.id = 'main';
  page.innerHTML = `
  <form class="fnav-account-step" data-step="reset" novalidate>
    <h1 class="fnav-account-title" tabindex="-1">Reset your password</h1>
    <p class="fnav-account-copy">Enter and confirm a new password below to reset your password.</p>
    ${field('reset', { name: 'password', type: 'password', placeholder: 'Password', label: 'New password', autocomplete: 'new-password', show: true })}
    ${field('reset', { name: 'confirm', type: 'password', placeholder: 'Confirm Password', label: 'Confirm new password', autocomplete: 'new-password', show: true })}
    ${submitBtn('Reset password')}
    <p class="fnav-account-copy"><a href="${SHOP_PAGE}">Nevermind, return to shopping</a></p>
  </form>
  <p class="fnav-account-sr" aria-live="polite" data-live></p>`;
  el.replaceWith(page);
  const form = page.querySelector('form');
  const live = page.querySelector('[data-live]');

  wireShowToggles(page);

  /* first screen — the form's rows rise in like a template's intro */
  const rows = [...form.children];
  const m = mo();
  const reduced = isReduced();
  animate(rows, reduced ? { opacity: [0, 1] } : { opacity: [0, 1], y: [m.riseSm, 0] }, {
    duration: reduced ? m.quick : m.slow, ease: m.eEntrance, delay: stagger(m.stagTight, { startDelay: 0.1 }),
  });
  setTimeout(() => rows.forEach((r) => { r.style.opacity = ''; r.style.transform = ''; }), (0.1 + m.stagTight * rows.length + m.slow) * 1000 + 150);

  watchFixes(form, RULES.reset);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (busy) return;
    const failed = validate(form, RULES.reset);
    if (failed.length) {
      const first = controlsFor(form, failed[0][0])[0];
      shake(boxOf(first));
      first.focus();
      live.textContent = failed.map(([, msg]) => msg).join(' ');
      return;
    }
    busy = true;
    const restore = await working(form.querySelector('.fnav-account-submit'));
    restore();
    busy = false;
    form.reset();
    maskAll(page);
    live.textContent = 'Your password has been reset. Log in with your new password.';
    openFigsAccount('login');
  });
}
